import React from 'react';

import {useSelector} from 'react-redux';

import {CreateDocumentButton} from 'app/components/general/CreateDocumentButton';
import {DashboardPage} from 'app/components/general/DashboardPage';
import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import {DashboardSidebarTagsList} from 'app/components/general/DashboardPage/DashboardSidebarTagsList';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {DocumentBulkActionsButton} from 'app/components/general/DocumentBulkActionsButton';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {FolderLink} from 'app/folders/components/FolderLink';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useProcesses} from 'app/process/api';
import {CompactProcessListItem} from 'app/process/components/CompactProcessListItem';
import {useProcessFilters} from 'app/process/hooks';
import {toggleDashboardRelation} from 'app/process/slice';
import {PROCESS_CREATE_PAGE_URL, PROCESS_DASHBOARD_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  ProcessDashboardFetchParamsType,
  ProcessLocationParamsType,
} from 'app/process/types';
import type {WithRouterProps} from 'react-router';
import type {ContentType, ProcessSerializedType} from 'sweet-types';

export const ProcessesDashboard = (
  routerProps: WithRouterProps<unknown, ProcessLocationParamsType>
) => {
  const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);
  const currentUser = useCurrentUserState().data!;

  const actions = useAsyncActions({toggleDashboardRelation});
  const activeRelations = useSelector((state) => state.processes.relations);

  return (
    <DashboardPage<
      ProcessSerializedType,
      ProcessLocationParamsType,
      ProcessDashboardFetchParamsType
    >
      {...routerProps}
      actionArea={
        <CreateDocumentButton
          contentType="proceduregroup"
          disabled={!isTeamMember}
        />
      }
      sidebar={
        <>
          <DashboardSidebarFoldersList />
          <DashboardSidebarTagsList />
        </>
      }
      header={({resetFilters, params, count}) =>
        params.folder != null ? (
          <div className="my-2 text-center">
            <h4>
              <i className="far fa-folder-open" /> {gt('Folder contents')}
              <span>
                <FolderLink hashid={params.folder} className="small ml-3" />
              </span>
            </h4>
            {count != 0 ? (
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all processes.')}
              </button>
            ) : null}
          </div>
        ) : null
      }
      useDataHook={useProcesses}
      useFiltersHook={useProcessFilters}
      contentType={'proceduregroup'}
      enableNotifications={true}
      enableBulkSelect={currentUser.is_manager}
      filterbar={({resetFilters, viewingAll}) => (
        <Filterbar
          ct="proceduregroup"
          resetFilters={resetFilters}
          viewingAll={viewingAll}
          hideFilters={[
            'private',
            'public',
            'is_in_knowledgebase',
            'is_not_in_knowledgebase',
          ]}
          show={{
            team: true,
            tags: true,
            sort: true,
            filters: true,
            search: true,
          }}
        />
      )}
      renderItem={(
        process,
        {params, refetch},
        isSelected,
        addToSelection,
        removeFromSelection,
        notifications
      ) => (
        <CompactProcessListItem
          key={process.id}
          object={process}
          refreshData={refetch}
          sortBy={params.ordering}
          notifications={notifications}
          activeRelations={activeRelations}
          toggleRelation={(contentType: ContentType, isOpen: boolean) =>
            actions.toggleDashboardRelation({
              contentType,
              isOpen,
            })
          }
          enableBulkSelect={currentUser.is_manager}
          isSelected={isSelected}
          addToSelection={addToSelection}
          removeFromSelection={removeFromSelection}
        />
      )}
      onModalChangeMessage={({refetch}) =>
        (message, {runWhenAnyModelMatch}) => {
          runWhenAnyModelMatch(message, ['process', 'processversion'], () =>
            refetch()
          );
        }}
      messages={{
        counter: (count) => ngt('%s process', '%s processes', count),
        topListActions: (selectedDocsOnThisPage, {refetch}) => (
          <DocumentBulkActionsButton
            documents={selectedDocsOnThisPage.map((selectedDoc) => ({
              document_content_type: selectedDoc.content_type,
              document: selectedDoc,
              url: selectedDoc.url,
              can_edit: selectedDoc.can_edit,
              can_delete: selectedDoc.can_delete,
              object_id: selectedDoc.id,
            }))}
            refreshData={refetch}
            enableBulkSelect={currentUser.is_manager}
            excludeActions={['knowledge-base']}
          />
        ),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon="fa-copy"
              head={gt('No processes on your account')}
            >
              {isTeamMember ? (
                <FullHTMLLink
                  className="btn btn-primary"
                  html_url={PROCESS_CREATE_PAGE_URL}
                >
                  {gt('Create a Process')}
                </FullHTMLLink>
              ) : null}
            </NoResultsMessage>
          ) : (
            <NoResultsMessage
              icon="fa-copy"
              head={gt('No processes found with these filters')}
            >
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all processes')}
              </button>
            </NoResultsMessage>
          ),
      }}
      subscribeToPresence={{
        contentType: 'proceduregroup',
        objectsIds: (processes) => processes.map((process) => process.id),
      }}
    />
  );
};

const processCt = getContentType('proceduregroup');

export const ProcessesDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={processCt.icon}
    text={gt('Processes')}
    link={PROCESS_DASHBOARD_URL}
  />
);
